/**
 * Adawat custom product page
 * Gallery, variant selection, add-to-cart, often-bought, lightbox
 *
 * Add-to-cart follows Horizon's CartLinesUpdateEvent flow (same as product-form.js)
 * so the cart drawer morphs + opens without a delayed generic refresh.
 */
import { addItemsToCart, openCartDrawer } from '@theme/adawat-cart';
import { cartMeetsMinimum } from '@theme/adawat-min-order';

const MONEY_FORMAT =
  (window.theme && window.theme.moneyFormat) ||
  (window.Shopify && window.Shopify.money_format) ||
  '{{amount}}';

function formatMoney(cents, format) {
  const value = Math.round(Number(String(cents).replace(/[^\d.-]/g, '')) || 0);
  if (typeof window.adawatFormatMoney === 'function') return window.adawatFormatMoney(value);
  const major = value / 100;
  const fmt = format || MONEY_FORMAT || '${{amount}}';
  return String(fmt).replace(/\{\{\s*amount\s*\}\}/, major.toFixed(2));
}

function escapePdpText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function optionValueName(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return value.name || value.value || String(value);
}

function cellText(cell) {
  return (cell?.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function cellHtml(cell) {
  if (!cell) return '';
  return String(cell.innerHTML || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function descriptionHasRichText(box) {
  if (!box) return false;
  return Boolean(
    box.querySelector(
      'strong, b, em, i, u, s, del, a[href], ul, ol, li, blockquote, img, h1, h2, h3, h4, h5, h6, mark'
    )
  );
}

function isSpecHeaderRow(label, value) {
  const left = label.toLowerCase();
  const right = value.toLowerCase();
  return (
    (left === 'feature' || left === 'features' || left === 'specification' || left === 'الميزة') &&
    (right === 'details' || right === 'detail' || right === 'value' || right === 'التفاصيل')
  );
}

function collectSpecRows(table) {
  const rows = [];
  table.querySelectorAll('tr').forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll('th, td'));
    if (cells.length < 2) return;
    const label = cellText(cells[0]);
    const labelHtml = cellHtml(cells[0]) || escapePdpText(label);
    const valueText = cells
      .slice(1)
      .map((cell) => cellText(cell))
      .filter(Boolean)
      .join(' ');
    const valueHtml = cells
      .slice(1)
      .map((cell) => cellHtml(cell))
      .filter(Boolean)
      .join(' ');
    if (!label || !valueText) return;
    if (isSpecHeaderRow(label, valueText)) return;
    rows.push({ label, labelHtml, value: valueText, valueHtml: valueHtml || escapePdpText(valueText) });
  });
  return rows;
}

function normalizeDescriptionIntro(box) {
  const clone = box.cloneNode(true);
  clone.querySelectorAll('table, [class*="tableContainer"], [class*="tableWrapper"]').forEach((el) => el.remove());
  clone.querySelectorAll('[class*="selectionAnchor"]').forEach((el) => {
    if (!el.textContent.trim()) el.remove();
  });
  clone.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
    const text = cellText(heading);
    if (!text) {
      heading.remove();
      return;
    }
    if (/^product specifications$/i.test(text) || text === 'المواصفات') {
      const h = document.createElement('h3');
      h.innerHTML = heading.innerHTML;
      heading.replaceWith(h);
      return;
    }
    if (text.length > 60) {
      const p = document.createElement('p');
      p.className = 'adawat-pdp__desc-intro';
      p.innerHTML = heading.innerHTML;
      heading.replaceWith(p);
    }
  });
  clone.querySelectorAll('div, span').forEach((el) => {
    if (el.querySelector('p, h1, h2, h3, h4, h5, h6, strong, em, b, i, u, a, ul, ol')) return;
    if (!el.textContent.trim()) el.remove();
  });
  return clone.innerHTML.trim();
}

function formatHtmlSpecTables(box) {
  const tables = Array.from(box.querySelectorAll('table'));
  if (!tables.length) return false;

  // Keep original rich HTML inside table cells — only normalize layout classes.
  if (descriptionHasRichText(box)) {
    tables.forEach((table) => table.classList.add('adawat-pdp__spec-table'));
    box.dataset.specFormatted = 'true';
    return true;
  }

  const rows = tables.flatMap((table) => collectSpecRows(table));
  if (!rows.length) {
    tables.forEach((table) => table.classList.add('adawat-pdp__spec-table'));
    box.dataset.specFormatted = 'true';
    return true;
  }

  const introHtml = normalizeDescriptionIntro(box);
  const rowsHtml = rows
    .map(
      (row) =>
        `<tr><th scope="row">${row.labelHtml}</th><td>${row.valueHtml}</td></tr>`
    )
    .join('');
  box.innerHTML = `${introHtml}<table class="adawat-pdp__spec-table"><tbody>${rowsHtml}</tbody></table>`;
  box.dataset.specFormatted = 'true';
  return true;
}

function formatPdpDescription(root) {
  const box = root?.querySelector?.('[data-adawat-description]');
  if (!box || box.dataset.specFormatted === 'true') return;

  // Never strip bold/italic/lists/links from admin rich text.
  if (box.classList.contains('rte') && descriptionHasRichText(box) && !box.querySelector('table')) {
    box.dataset.specFormatted = 'true';
    return;
  }

  const nativeTables = Array.from(box.querySelectorAll('table')).filter(
    (table) => !table.classList.contains('adawat-pdp__spec-table') || !table.querySelector('tbody tr')
  );
  const hasNativeTable =
    box.hasAttribute('data-adawat-html-table') ||
    nativeTables.some((table) => table.querySelector('th, td'));
  if (hasNativeTable && formatHtmlSpecTables(box)) return;

  if (descriptionHasRichText(box)) {
    box.dataset.specFormatted = 'true';
    return;
  }

  const tableBody = box.querySelector('.adawat-pdp__spec-table tbody');
  if (tableBody?.querySelector('tr')) {
    box.dataset.specFormatted = 'true';
    const emptyTableNote = box.querySelector('.adawat-pdp__desc-fallback');
    if (emptyTableNote) emptyTableNote.remove();
    return;
  }

  const source = box.querySelector('.adawat-pdp__desc-fallback') || box;
  const html = source.innerHTML || '';
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/div>/gi, '\n');
  const tmp = document.createElement('div');
  tmp.innerHTML = withBreaks;
  let text = (tmp.textContent || '').replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').trim();
  if (!text) return;

  text = text.replace(/[★▪•]/g, '✦');
  const keySplit = text.split(/key features:?/i);
  let intro = keySplit.length > 1 ? keySplit[0].trim() : '';
  let rest = keySplit.length > 1 ? keySplit.slice(1).join('\n') : text;
  rest = rest.replace(/^[:\s]+/, '');

  const chunks = rest
    .split(/\s*✦\s*|\n+/)
    .map((s) => s.replace(/^key features:?/i, '').trim())
    .filter(Boolean);

  const rows = [];
  const leftover = [];
  chunks.forEach((chunk) => {
    const match = chunk.match(/^(.{2,48}?)\s*[:：]\s+(.+)$/s);
    if (match) {
      rows.push({ label: match[1].replace(/\s+/g, ' ').trim(), value: match[2].replace(/\s+/g, ' ').trim() });
      return;
    }
    leftover.push(chunk);
  });

  if (!intro && leftover.length) {
    intro = leftover.filter((p) => p.length > 28).join(' ');
  }
  if (!rows.length) return;

  const introHtml = intro
    ? `<p class="adawat-pdp__desc-intro">${escapePdpText(intro)}</p>`
    : '';
  const rowsHtml = rows
    .map(
      (row) =>
        `<tr><th scope="row">${escapePdpText(row.label)}</th><td>${escapePdpText(row.value)}</td></tr>`
    )
    .join('');

  box.innerHTML = `${introHtml}<table class="adawat-pdp__spec-table"><tbody>${rowsHtml}</tbody></table>`;
  box.dataset.specFormatted = 'true';
}

function formatDeliveryRange(el) {
  if (!el) return;
  const startOffset = Number(el.dataset.startDays || 2);
  const endOffset = Number(el.dataset.endDays || 4);
  const label = `Fast estimated delivery · ${startOffset}-${endOffset} days`;
  const textEl = el.querySelector('[data-adawat-delivery-text]');
  if (textEl) textEl.textContent = label;
  else el.append(label);
}

  function initPdp(root) {
    if (!root || root.dataset.adawatPdpInit === 'true') return;
    root.dataset.adawatPdpInit = 'true';

    formatPdpDescription(root);
    formatDeliveryRange(root.querySelector('[data-adawat-delivery]'));

    const productJsonEl = root.querySelector('[data-adawat-product-json]');
    const productData = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
    if (!productData) return;

    const moneyFormat = root.dataset.moneyFormat || MONEY_FORMAT;

    function previewVariant() {
      if (optionsComplete()) {
        const found = findVariant(selectedOptions);
        if (found) return found;
      }
      const currentId = Number(productData.currentVariant?.id || 0);
      if (currentId) {
        return (
          productData.variants.find((row) => Number(row.id) === currentId) || productData.currentVariant
        );
      }
      return productData.variants.find((row) => row.available) || productData.variants[0] || null;
    }

    function isExcludedFromSitewide() {
      if (root.getAttribute('data-adawat-sale-excluded') === 'true') return true;
      const cfg = window.__ADAWAT_SITEWIDE_SALE__ || {};
      const rules = window.__ADAWAT_SALE_RULES__ || {};
      const excludeTags = new Set(
        ['no-discount', 'no discount', ...(cfg.exclude_tags || []), ...(rules.exclude_tags || []), ...String(document.body?.dataset?.saleExcludeTags || '').split(',')]
          .map((tag) => String(tag || '').toLowerCase().trim())
          .filter(Boolean)
      );
      const productTags = String(root.dataset.adawatTags || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);
      if (excludeTags.size && productTags.some((tag) => excludeTags.has(tag))) return true;
      const collections = String(root.dataset.adawatCollections || '')
        .split(/[|,]/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      if (collections.some((handle) => String(handle || '').includes('no-discount'))) return true;
      const listed = [
        'back-to-school',
        'no-discount',
        'no-discount-for-1-week-collection',
        ...(cfg.exclude_handles || []),
        ...(rules.exclude_handles || []),
        ...String(document.body?.dataset?.saleExclude || '').split(','),
      ];
      const excluded = new Set(listed.map((handle) => String(handle || '').toLowerCase().trim()).filter(Boolean));
      const sitewide = new Set(
        String(document.body?.dataset?.sitewideCollections || '')
          .split(',')
          .map((part) => part.trim().toLowerCase())
          .filter(Boolean)
          .concat([
            'all-products-excluding-collections',
            'all-products-excluding-collection',
            'all-products-excluding',
            'all-products-excluding-collections-copy',
          ])
      );
      if (collections.some((handle) => excluded.has(handle) && !sitewide.has(handle))) return true;
      const path = String(window.location.pathname || '').toLowerCase();
      const pathHandle = (path.match(/\/collections\/([^/?#]+)/) || [])[1];
      if (pathHandle) {
        let decoded = pathHandle;
        try {
          decoded = decodeURIComponent(pathHandle);
        } catch (error) {}
        if (excluded.has(decoded) && !sitewide.has(decoded)) return true;
      }
      return Array.from(excluded).some((handle) => {
        if (sitewide.has(handle)) return false;
        return Boolean(root.querySelector(`.adawat-pdp__crumbs a[href*="/collections/${handle}"]`));
      });
    }

    function clampSale(value) {
      const n = Math.round(Number(value) || 0);
      if (n < 1 || n > 95) return 0;
      return n;
    }

    function rulesPercent() {
      const raw = window.__ADAWAT_SALE_RULES__;
      const layers =
        raw && Array.isArray(raw.discounts) && raw.discounts.length ? raw.discounts : raw?.percent ? [raw] : [];
      const productId = String(root.dataset.productId || '');
      const collections = String(root.dataset.adawatCollections || '')
        .split(/[|,]/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      let max = 0;
      layers.forEach((layer) => {
        const pct = clampSale(layer?.percent);
        if (!pct) return;
        const ids = (layer.product_ids || layer.productIds || []).map(String);
        const handles = (layer.collection_handles || layer.collectionHandles || [])
          .map((handle) => String(handle || '').toLowerCase().trim())
          .filter(Boolean);
        if (productId && ids.includes(productId)) max = Math.max(max, pct);
        else if (handles.some((handle) => collections.includes(handle))) max = Math.max(max, pct);
      });
      return max;
    }

    function inSitewideCollection() {
      const listed = String(document.body?.dataset?.sitewideCollections || '')
        .split(',')
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      const cfg = window.__ADAWAT_SITEWIDE_SALE__ || {};
      const handles = new Set(
        [
          ...listed,
          String(cfg.collection_handle || '').toLowerCase().trim(),
          ...((cfg.collection_handles || []).map((h) => String(h || '').toLowerCase().trim())),
          'all-products-excluding-collections',
          'all-products-excluding-collection',
          'all-products-excluding',
        ].filter(Boolean)
      );
      const collections = String(root.dataset.adawatCollections || '')
        .split(/[|,]/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      return collections.some((handle) => handles.has(handle));
    }

    function sitewideSaleOn() {
      const cfg = window.__ADAWAT_SITEWIDE_SALE__;
      if (cfg && typeof cfg === 'object') {
        if (cfg.enabled === false || cfg.enabled === 'false' || cfg.enabled === 0 || cfg.enabled === '0') {
          return false;
        }
        if (cfg.enabled === true || cfg.enabled === 'true' || cfg.enabled === 1 || cfg.enabled === '1') {
          return clampSale(cfg.percent) > 0;
        }
        return clampSale(cfg.percent) > 0;
      }
      return clampSale(document.body?.dataset?.sitewidePercent) > 0;
    }

    function salePercent() {
      const tags = String(root.dataset.adawatTags || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);
      const collections = String(root.dataset.adawatCollections || '')
        .split(/[|,]/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
      if (
        tags.includes('no-discount') ||
        tags.includes('no discount') ||
        collections.some((handle) => handle.includes('no-discount'))
      ) {
        return 0;
      }

      const rules = rulesPercent();
      const sitewideOn = sitewideSaleOn();
      const cfg = window.__ADAWAT_SITEWIDE_SALE__ || {};
      const excluded = isExcludedFromSitewide();
      const allItems = cfg.all_items === true || cfg.allItems === true;
      const sitewideHandles = [
        String(cfg.collection_handle || '').toLowerCase().trim(),
        ...((cfg.collection_handles || []).map((handle) => String(handle || '').toLowerCase().trim())),
      ].filter(Boolean);
      const inSitewide = sitewideHandles.length ? inSitewideCollection() : false;
      const sitewide =
        sitewideOn && !excluded && (allItems || inSitewide)
          ? clampSale(document.body?.dataset?.sitewidePercent) || clampSale(cfg.percent)
          : 0;

      const variant = previewVariant();
      const id = Number(variant?.id || 0);
      const byVariant = window.__ADAWAT_SALE_BY_VARIANT__ || {};
      const specific = id ? clampSale(byVariant[String(id)]) : 0;
      const globalSitewide = clampSale(document.body?.dataset?.sitewidePercent) || clampSale(cfg.percent);
      let baked = clampSale(root.dataset.adawatSalePercent);
      if (root.dataset.adawatSaleBasis === 'compare') baked = 0;
      if (!sitewide && baked === globalSitewide) baked = 0;
      const percent = Math.max(rules, sitewide, specific, excluded ? 0 : baked);
      if (!percent && root.dataset.adawatSaleBasis !== 'compare') {
        root.removeAttribute('data-adawat-sale-percent');
      }
      return percent;
    }

    function sitewidePrice(cents) {
      const value = Number(cents) || 0;
      const percent = salePercent();
      if (!percent) return value;
      return Math.round((value * (100 - percent)) / 100);
    }

    function sitewideCompare(unit) {
      const price = Number(unit) || 0;
      if (!salePercent()) return 0;
      return price;
    }

    function paintSalePrice(variant) {
      const unit = Number(variant?.price || 0);
      if (variant?.id) {
        root.setAttribute('data-variant-id', String(variant.id));
        window.__ADAWAT_PDP_LIST_CENTS__ = unit;
      }
      if (unit > 0) root.setAttribute('data-price-cents', String(unit));
      if (unit > 0) root.setAttribute('data-price-dollars', (unit / 100).toFixed(2));
      // Automatic % remultiplies catalog price (matches cart). Compare-at alone = strikethrough, no invented badge %.
      const checkout = salePercent();
      const compareAt = Number(variant?.compare_at_price || 0);
      const adminCompareSale = compareAt > unit && unit > 0;
      const percent = checkout > 0 ? checkout : 0;
      const salePrice = checkout > 0 ? Math.round((unit * (100 - checkout)) / 100) : unit;
      const onSale = (checkout > 0 && unit > salePrice) || (!checkout && adminCompareSale);
      const complete = Boolean(optionsComplete() && findVariant(selectedOptions));
      if (checkout > 0) {
        root.dataset.adawatSalePercent = String(checkout);
        root.dataset.adawatSaleBasis = 'auto';
      } else if (adminCompareSale) {
        root.removeAttribute('data-adawat-sale-percent');
        root.dataset.adawatSaleBasis = 'compare';
      } else {
        root.removeAttribute('data-adawat-sale-percent');
        root.removeAttribute('data-adawat-sale-basis');
      }
      if (priceEl && unit > 0) {
        const money = formatMoney(salePrice, moneyFormat);
        priceEl.textContent = requireOptions && !complete ? `From ${money}` : money;
      }
      if (compareEl) {
        if (onSale) {
          const was = checkout > 0 ? unit : compareAt;
          compareEl.textContent = formatMoney(was, moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      const saleBadge = root.querySelector('[data-adawat-sale-badge]');
      const priceBadge = root.querySelector('[data-adawat-price-badge]');
      const badgeText = percent > 0 ? `-${percent}%` : '';
      if (saleBadge) {
        if (percent > 0) {
          saleBadge.hidden = false;
          saleBadge.removeAttribute('hidden');
          saleBadge.textContent = badgeText;
        } else {
          saleBadge.hidden = true;
          saleBadge.textContent = '';
          saleBadge.setAttribute('hidden', '');
        }
      }
      if (priceBadge) {
        if (onSale && percent > 0) {
          priceBadge.hidden = false;
          priceBadge.textContent = badgeText;
        } else {
          priceBadge.hidden = true;
          priceBadge.textContent = '';
          priceBadge.setAttribute('hidden', '');
        }
      }
      root.querySelector('[data-adawat-price-wrap]')?.classList.toggle('adawat-pdp__price--on-sale', onSale);
    }
    const carousel = root.querySelector('[data-adawat-carousel]');
    const slides = Array.from(root.querySelectorAll('[data-adawat-slide]'));
    let mainImg = root.querySelector('[data-adawat-main-image]');
    const thumbs = Array.from(root.querySelectorAll('[data-adawat-thumb]'));
    const priceEl = root.querySelector('[data-adawat-price]');
    const compareEl = root.querySelector('[data-adawat-compare-price]');
    const variantInput = root.querySelector('[data-adawat-variant-id]');
    const atcBtn = root.querySelector('[data-adawat-atc]');
    const atcLabel = root.querySelector('[data-adawat-atc-label]');
    const buyNowBtn = root.querySelector('[data-adawat-buy-now]');
    const formError = root.querySelector('[data-adawat-form-error]');
    const form = root.querySelector('[data-adawat-product-form]');
    const qtyInput = root.querySelector('[data-adawat-qty-input]');
    const qtyMinus = root.querySelector('[data-adawat-qty-minus]');
    const qtyPlus = root.querySelector('[data-adawat-qty-plus]');
    const optionGroups = Array.from(root.querySelectorAll('[data-adawat-option]'));
    const deliveryEl = root.querySelector('[data-adawat-delivery]');
    const stockEl = root.querySelector('[data-adawat-stock]');
    const stockTextEl = root.querySelector('[data-adawat-stock-text]');
    const stockFillEl = root.querySelector('[data-adawat-stock-fill]');
    const lightbox = document.querySelector('[data-adawat-lightbox]');
    const lightboxImg = lightbox?.querySelector('[data-adawat-lightbox-img]');
    const zoomTriggers = root.querySelectorAll('[data-adawat-zoom]');
    let carouselDragged = false;
    let syncingFromScroll = false;

    const requireOptions =
      productData.requireOptions === true || form?.dataset.requireOptions === 'true';
    const addToCartLabel = atcBtn?.dataset.addLabel || 'Add to Cart';
    const soldOutLabel = atcBtn?.dataset.soldOutLabel || 'Sold out';

    function anyVariantAvailable() {
      return (productData.variants || []).some((row) => row.available);
    }

    function selectOptionsLabel() {
      const missing = missingOptionNames();
      if (missing.length === 1) return `Select ${missing[0]}`;
      return atcBtn?.dataset.selectLabel || 'Select options';
    }

    function setDeliveryVisible(show) {
      if (deliveryEl) deliveryEl.hidden = !show;
    }

    let selectedOptions = productData.options.map((_, i) => {
      if (requireOptions) return null;
      const v = productData.currentVariant;
      if (v) return v[`option${i + 1}`];
      const firstValue = productData.options[i]?.values?.[0];
      return firstValue != null ? optionValueName(firstValue) : null;
    });

    function optionsComplete() {
      return selectedOptions.length > 0 && selectedOptions.every((val) => val != null && val !== '');
    }

    function missingOptionNames() {
      return optionGroups
        .filter((group) => {
          const idx = Number(group.dataset.optionIndex);
          return !selectedOptions[idx];
        })
        .map((group) => group.dataset.optionName || 'option');
    }

    function promptSelectOptions() {
      const missing = missingOptionNames();
      const message =
        missing.length === 1
          ? `Please select ${missing[0]} before adding to cart.`
          : `Please select ${missing.join(' and ')} before adding to cart.`;
      if (formError) {
        formError.textContent = message;
        formError.hidden = false;
        formError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      optionGroups.forEach((group) => {
        const idx = Number(group.dataset.optionIndex);
        group.classList.toggle('is-incomplete', !selectedOptions[idx]);
      });
      const firstMissing = root.querySelector('.adawat-pdp__option.is-incomplete');
      firstMissing?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function clearOptionPrompt() {
      optionGroups.forEach((group) => group.classList.remove('is-incomplete'));
      if (formError && !formError.dataset.keepError) {
        formError.hidden = true;
        formError.textContent = '';
      }
    }

    function findVariant(options) {
      if (!options.every((val) => val != null && val !== '')) return null;
      return productData.variants.find((variant) =>
        options.every((val, idx) => variant[`option${idx + 1}`] === val)
      );
    }

    function mediaForVariant(variant) {
      if (!variant) return productData.media[0];
      if (variant.featured_media) {
        return productData.media.find((m) => m.id === variant.featured_media.id) || productData.media[0];
      }
      if (variant.featured_image) {
        return (
          productData.media.find((m) => m.id === variant.featured_image.id) ||
          productData.media.find((m) => m.src && variant.featured_image.src && m.src.includes(variant.featured_image.src.split('?')[0].split('/').pop())) ||
          productData.media[0]
        );
      }
      return productData.media[0];
    }

    function slideByMediaId(mediaId) {
      return slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId)) || null;
    }

    let markedMediaId = '';

    function markActiveSlide(slide, { scrollThumb = true } = {}) {
      if (!slide) return;
      const mediaId = String(slide.dataset.mediaId || '');
      const alreadyActive = mediaId && mediaId === markedMediaId;
      if (!alreadyActive) {
        markedMediaId = mediaId;
        slides.forEach((item) => {
          const active = item === slide;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
          const img = item.querySelector('[data-adawat-slide-image]');
          if (!img) return;
          if (active) {
            img.setAttribute('data-adawat-main-image', '');
            mainImg = img;
          } else {
            img.removeAttribute('data-adawat-main-image');
          }
        });

        thumbs.forEach((thumb) => {
          const active = String(thumb.dataset.mediaId) === mediaId;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      if (scrollThumb) {
        const activeThumb = thumbs.find((thumb) => String(thumb.dataset.mediaId) === mediaId);
        activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    }

    function nearestSlide() {
      if (!carousel || !slides.length) return null;
      const left = carousel.scrollLeft;
      let best = slides[0];
      let bestDist = Infinity;
      slides.forEach((slide) => {
        const dist = Math.abs(slide.offsetLeft - left);
        if (dist < bestDist) {
          bestDist = dist;
          best = slide;
        }
      });
      return best;
    }

    function setMainMedia(media, { animate = true } = {}) {
      if (!media) return;
      const slide = slideByMediaId(media.id);
      if (!slide || !carousel) {
      const src = media.src || media.preview_image?.src;
        if (!src || !mainImg) return;
      mainImg.src = src;
      mainImg.srcset = '';
      mainImg.dataset.mediaId = String(media.id);
      mainImg.alt = media.alt || productData.title;
        return;
      }

      markActiveSlide(slide, { scrollThumb: true });
      const left = slide.offsetLeft;
      if (Math.abs(carousel.scrollLeft - left) < 2) return;
      syncingFromScroll = true;
      carousel.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
      window.setTimeout(() => {
        syncingFromScroll = false;
      }, animate ? 420 : 0);
    }

    function stepMainMedia(delta) {
      if (!slides.length) return false;
      const current = nearestSlide() || slides[0];
      const idx = Math.max(0, slides.indexOf(current));
      const next = slides[(idx + delta + slides.length) % slides.length];
      if (!next) return false;
      const media = productData.media.find((m) => String(m.id) === String(next.dataset.mediaId));
      if (media) setMainMedia(media, { animate: true });
      else markActiveSlide(next);
      return true;
    }

    function bindMainCarousel() {
      if (!carousel || slides.length < 1) return;

      const featuredId =
        mainImg?.dataset?.mediaId ||
        slides.find((s) => s.classList.contains('is-active'))?.dataset?.mediaId ||
        slides[0]?.dataset?.mediaId;
      const featuredSlide = slideByMediaId(featuredId);
      if (featuredSlide) {
        markActiveSlide(featuredSlide, { scrollThumb: false });
        carousel.scrollLeft = featuredSlide.offsetLeft;
      }

      let scrollTick = 0;
      let settleTimer = 0;
      let swipeActive = false;

      function syncActiveFromScroll({ thumbs = false } = {}) {
        const slide = nearestSlide();
        if (!slide) return;
        markActiveSlide(slide, { scrollThumb: thumbs });
      }

      function scheduleSettleSync() {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          swipeActive = false;
          syncActiveFromScroll({ thumbs: true });
        }, 140);
      }

      carousel.addEventListener(
        'scroll',
        () => {
          if (syncingFromScroll) return;
          window.cancelAnimationFrame(scrollTick);
          scrollTick = window.requestAnimationFrame(() => {
            /* Never scrollIntoView thumbs mid-swipe — that blinks the gallery. */
            syncActiveFromScroll({ thumbs: false });
          });
          if (!swipeActive) scheduleSettleSync();
        },
        { passive: true }
      );

      if ('onscrollend' in window) {
        carousel.addEventListener(
          'scrollend',
          () => {
            swipeActive = false;
            window.clearTimeout(settleTimer);
            syncActiveFromScroll({ thumbs: true });
          },
          { passive: true }
        );
      }

      const isInteractiveTarget = (target) =>
        Boolean(
          target instanceof Element &&
            target.closest(
              'button, a, input, textarea, select, label, [data-wishlist-toggle], [data-compare-toggle]'
            )
        );

      /* Mouse / pen drag — touch uses native scroll-snap for a continuous carousel.
         Defer pointer capture so native image drag-out (WhatsApp / desktop) still works. */
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let dragPending = false;
      let nativeImageDrag = false;

      carousel.addEventListener('dragstart', (event) => {
        const img = event.target instanceof Element ? event.target.closest('img[data-adawat-slide-image]') : null;
        if (!img) return;
        nativeImageDrag = true;
        dragPending = false;
        dragging = false;
        carouselDragged = false;
        carousel.classList.remove('is-dragging');
        const url = img.currentSrc || img.src || '';
        try {
          if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'copy';
            if (url) {
              event.dataTransfer.setData('text/uri-list', url);
              event.dataTransfer.setData('text/plain', url);
              const name =
                String(img.alt || 'product')
                  .replace(/[^\w\-]+/g, '_')
                  .slice(0, 40) || 'product';
              event.dataTransfer.setData('DownloadURL', `image/jpeg:${name}.jpg:${url}`);
            }
          }
        } catch (_) {
          /* ignore */
        }
      });

      carousel.addEventListener('dragend', () => {
        nativeImageDrag = false;
        dragPending = false;
        dragging = false;
        pointerId = null;
        carousel.classList.remove('is-dragging');
        window.setTimeout(() => {
          carouselDragged = false;
        }, 0);
      });

      carousel.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch') return;
        if (event.button != null && event.button !== 0) return;
        if (isInteractiveTarget(event.target)) return;
        if (slides.length < 2) return;
        if (nativeImageDrag) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = carousel.scrollLeft;
        dragging = false;
        dragPending = true;
        swipeActive = true;
        carouselDragged = false;
      });

      carousel.addEventListener(
        'pointermove',
        (event) => {
          if (nativeImageDrag) return;
          if (event.pointerId !== pointerId) return;
          if (!dragPending && !dragging) return;
          const dx = event.clientX - startX;
          if (!dragging) {
            if (Math.abs(dx) < 10) return;
            dragging = true;
            dragPending = false;
            carousel.classList.add('is-dragging');
            try {
              carousel.setPointerCapture(event.pointerId);
            } catch (_) {
              /* ignore */
            }
          }
          if (Math.abs(dx) > 4) carouselDragged = true;
          carousel.scrollLeft = startScroll - dx;
          event.preventDefault();
        },
        { passive: false }
      );

      const endDrag = (event) => {
        if ((!dragging && !dragPending) || (event && event.pointerId !== pointerId)) return;
        const wasDragging = dragging;
        dragging = false;
        dragPending = false;
        pointerId = null;
        carousel.classList.remove('is-dragging');
        try {
          if (event?.pointerId != null) carousel.releasePointerCapture(event.pointerId);
        } catch (_) {
          /* ignore */
        }

        if (!wasDragging || nativeImageDrag) {
          swipeActive = false;
          window.setTimeout(() => {
            carouselDragged = false;
          }, 0);
          return;
        }

        const slide = nearestSlide();
        if (slide) {
          syncingFromScroll = true;
          carousel.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          markActiveSlide(slide, { scrollThumb: true });
          window.setTimeout(() => {
            syncingFromScroll = false;
            swipeActive = false;
          }, 320);
        } else {
          swipeActive = false;
        }

        window.setTimeout(() => {
          carouselDragged = false;
        }, 0);
      };

      carousel.addEventListener('pointerup', endDrag);
      carousel.addEventListener('pointercancel', endDrag);
      carousel.addEventListener('pointerleave', (event) => {
        if ((dragging || dragPending) && event.pointerType === 'mouse') endDrag(event);
      });

      let touchStartX = 0;
      carousel.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.touches?.[0]?.clientX || 0;
          carouselDragged = false;
          swipeActive = true;
          window.clearTimeout(settleTimer);
        },
        { passive: true }
      );
      carousel.addEventListener(
        'touchmove',
        (event) => {
          const x = event.touches?.[0]?.clientX || 0;
          if (Math.abs(x - touchStartX) > 10) carouselDragged = true;
        },
        { passive: true }
      );
      carousel.addEventListener(
        'touchend',
        () => {
          scheduleSettleSync();
          window.setTimeout(() => {
            carouselDragged = false;
          }, 80);
        },
        { passive: true }
      );
      carousel.addEventListener(
        'touchcancel',
        () => {
          scheduleSettleSync();
        },
        { passive: true }
      );

      carousel.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          stepMainMedia(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          stepMainMedia(1);
        }
      });
    }

    function updateAvailability() {
      optionGroups.forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        const buttons = group.querySelectorAll('[data-option-value]');
        buttons.forEach((btn) => {
          const value = btn.dataset.optionValue;
          const available = productData.variants.some((variant) => {
            if (variant[`option${optionIndex + 1}`] !== value) return false;
            if (!variant.available) return false;
            return selectedOptions.every((val, idx) => {
              if (idx === optionIndex) return true;
              if (!val) return true;
              return variant[`option${idx + 1}`] === val;
            });
          });
          btn.classList.toggle('is-unavailable', !available);
          btn.disabled = false;
        });
      });
    }

    function updateUI() {
      const complete = optionsComplete();
      const variant = complete ? findVariant(selectedOptions) : null;
      const saleVariant = variant || previewVariant();

      optionGroups.forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        const selectedLabel = group.querySelector('[data-selected-value]');
        const value = selectedOptions[optionIndex];
        if (selectedLabel) {
          if (value) {
            selectedLabel.textContent = value;
            selectedLabel.removeAttribute('data-placeholder');
          } else {
            selectedLabel.textContent = 'Select';
            selectedLabel.setAttribute('data-placeholder', 'true');
          }
        }
        group.querySelectorAll('[data-option-value]').forEach((btn) => {
          const isSelected = Boolean(value) && btn.dataset.optionValue === value;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
        if (value) group.classList.remove('is-incomplete');
      });

      paintSalePrice(saleVariant);

      if (!anyVariantAvailable()) {
        if (variantInput) variantInput.value = '';
        if (atcBtn && atcLabel) {
          atcBtn.disabled = true;
          atcLabel.textContent = soldOutLabel;
        }
        if (buyNowBtn) buyNowBtn.disabled = true;
        setDeliveryVisible(false);
        updateAvailability();
        return;
      }

      if (!complete || !variant) {
        if (variantInput) variantInput.value = '';
        if (atcBtn && atcLabel) {
          atcBtn.disabled = true;
          atcLabel.textContent = selectOptionsLabel();
        }
        if (buyNowBtn) buyNowBtn.disabled = true;
        setDeliveryVisible(false);
        updateAvailability();
        return;
      }

      if (variantInput) variantInput.value = variant.id;

      const media = mediaForVariant(variant);
      if (media) setMainMedia(media);

      if (atcBtn && atcLabel) {
        if (variant.available) {
          atcBtn.disabled = false;
          atcLabel.textContent = addToCartLabel;
        } else {
          atcBtn.disabled = true;
          atcLabel.textContent = soldOutLabel;
        }
      }
      if (buyNowBtn) buyNowBtn.disabled = !variant.available;
      setDeliveryVisible(Boolean(variant.available));

      updateBundlePrices(variant);

      if (stockEl && stockEl.dataset.tracked === 'true') {
        const qty = Math.max(0, Number(variant.inventory_quantity));
        if (!variant.available || Number.isNaN(qty) || qty <= 0) {
          stockEl.hidden = true;
        } else {
          stockEl.hidden = false;
          if (stockTextEl) {
            stockTextEl.textContent =
              qty <= 12
                ? `Hurry up, only ${qty} item${qty === 1 ? '' : 's'} left in stock.`
                : 'In stock — ready to ship.';
          }
          if (stockFillEl) {
            const fill = Math.max(8, Math.min(100, (qty / 20) * 100));
            stockFillEl.style.width = `${fill}%`;
          }
        }
      }

      updateAvailability();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Thumbs
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        const media = productData.media.find((m) => String(m.id) === String(mediaId));
        if (media) setMainMedia(media, { animate: true });
      });
    });

    bindMainCarousel();

    // Options
    optionGroups.forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      group.querySelectorAll('[data-option-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          selectedOptions[optionIndex] = btn.dataset.optionValue;
          clearOptionPrompt();
          updateUI();
        });
      });
    });

    // Lightbox
    function openLightbox(event) {
      const activeImg = nearestSlide()?.querySelector('[data-adawat-slide-image]') || mainImg;
      if (!lightbox || !lightboxImg || !activeImg) return;
      if (carouselDragged) {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        return;
      }
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (lightbox.parentElement !== document.body) {
        document.body.appendChild(lightbox);
      }

      lightboxImg.src = activeImg.currentSrc || activeImg.src;
      lightboxImg.alt = activeImg.alt || '';
      lightbox.scrollTop = 0;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');

      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      lightbox._scrollY = scrollY;
      document.documentElement.classList.add('adawat-lightbox-lock');
      document.body.style.top = `-${scrollY}px`;
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('adawat-lightbox-lock');
      const scrollY = lightbox._scrollY || 0;
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }

    zoomTriggers.forEach((el) => {
      el.addEventListener('click', openLightbox);
    });
    lightbox?.querySelector('[data-adawat-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightbox?.addEventListener('click', (e) => {
      if (e.target === lightbox) closeLightbox();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox?.classList.contains('is-open')) closeLightbox();
    });

    function getQuantity() {
      const n = Math.floor(Number(qtyInput?.value) || 1);
      return Math.max(1, Math.min(99, n));
    }

    function setQuantity(value) {
      if (!qtyInput) return;
      const next = Math.max(1, Math.min(99, Math.floor(Number(value) || 1)));
      qtyInput.value = String(next);
      if (qtyMinus) qtyMinus.disabled = next <= 1;
    }

    qtyMinus?.addEventListener('click', () => setQuantity(getQuantity() - 1));
    qtyPlus?.addEventListener('click', () => setQuantity(getQuantity() + 1));
    qtyInput?.addEventListener('change', () => setQuantity(qtyInput.value));
    qtyInput?.addEventListener('blur', () => setQuantity(qtyInput.value));
    setQuantity(1);

    const bundleRoot = root.querySelector('[data-adawat-bundle]');

    function bundleUnitPrice(variant) {
      const v = variant || (optionsComplete() ? findVariant(selectedOptions) : null) || {};
      return sitewidePrice(Number(v.price != null ? v.price : bundleRoot?.dataset.unitPrice || 0));
    }

    function bundleComparePrice(variant) {
      const v = variant || (optionsComplete() ? findVariant(selectedOptions) : null) || {};
      const rawUnit = Number(v.price != null ? v.price : bundleRoot?.dataset.unitPrice || 0);
      const unit = bundleUnitPrice(variant);
      const compare = sitewideCompare(rawUnit);
      return compare > unit ? compare : rawUnit;
    }

    function bundleCardTotals(card, unit, compare) {
      const percent = Math.min(100, Math.max(0, Number(card.dataset.bundlePercent) || 0));
      if (card.dataset.bundleOffer === 'true') {
        const buyQty = Math.max(1, Number(card.dataset.bundleBuyQty) || 1);
        const getQty = Math.max(1, Number(card.dataset.bundleGetQty) || 1);
        if (card.dataset.bundleSame !== 'false') {
          const paidExtra = Math.round((unit * getQty * (100 - percent)) / 100);
          return { total: unit * buyQty + paidExtra, compare: compare * (buyQty + getQty) };
        }
        const giftCents = sitewidePrice(Number(card.dataset.bundleGiftCents) || 0);
        const giftPaid = Math.round((giftCents * (100 - percent)) / 100);
        return { total: unit * buyQty + giftPaid, compare: unit * buyQty + giftCents };
      }
      const qty = Math.max(1, Number(card.dataset.bundleQty) || 1);
      const lineCompare = compare * qty;
      if (card.dataset.bundleMode === 'percentage' || percent > 0) {
        const total = Math.round((unit * qty * (100 - percent)) / 100);
        return { total, compare: lineCompare };
      }
      return { total: unit * qty, compare: lineCompare };
    }

    function parseBundleGifts(card) {
      try {
        const gifts = JSON.parse(card?.getAttribute('data-bundle-gifts') || '[]');
        return Array.isArray(gifts) ? gifts : [];
      } catch (error) {
        return [];
      }
    }

    function cartItemsFromBundle(variantId) {
      const selected = bundleRoot?.querySelector('.adawat-pdp-bundle__card.is-selected');
      const qty = Math.max(1, Number(selected?.dataset.bundleQty || getQuantity() || 1));
      const items = [{ id: variantId, quantity: qty }];
      if (selected?.dataset.bundleOffer === 'true') {
        parseBundleGifts(selected).forEach((gift) => {
          const id = Number(gift.id);
          const quantity = Math.max(1, Number(gift.quantity) || 1);
          if (!id) return;
          const existing = items.find((row) => row.id === id);
          if (existing) existing.quantity += quantity;
          else items.push({ id, quantity });
        });
      }
      return items;
    }

    function updateBundlePrices(variant) {
      if (!bundleRoot) return;
      const unit = bundleUnitPrice(variant);
      const compare = bundleComparePrice(variant);
      bundleRoot.dataset.unitPrice = String(unit);
      bundleRoot.dataset.comparePrice = String(compare);
      bundleRoot.querySelectorAll('[data-bundle-qty]').forEach((card) => {
        const totals = bundleCardTotals(card, unit, compare);
        const priceNode = card.querySelector('[data-bundle-price]');
        const compareNode = card.querySelector('[data-bundle-compare]');
        if (priceNode) priceNode.textContent = formatMoney(totals.total, moneyFormat);
        if (compareNode) {
          if (totals.compare > totals.total) {
            compareNode.textContent = formatMoney(totals.compare, moneyFormat);
            compareNode.hidden = false;
          } else {
            compareNode.hidden = true;
          }
        }
      });
      const selected = bundleRoot.querySelector('.adawat-pdp-bundle__card.is-selected');
      const totalEl = bundleRoot.querySelector('[data-bundle-total]');
      if (selected && totalEl) {
        totalEl.textContent = formatMoney(bundleCardTotals(selected, unit, compare).total, moneyFormat);
      }
    }

    function selectBundleCard(card) {
      if (!bundleRoot || !card) return;
      bundleRoot.querySelectorAll('[data-bundle-qty]').forEach((btn) => {
        const on = btn === card;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      setQuantity(card.dataset.bundleQty);
      updateBundlePrices();
    }

    async function applyBundleDiscountCode() {
      const code = String(bundleRoot?.dataset.bundleCode || '').trim();
      if (!code) return;
      const rootPath = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      try {
        await fetch(`${rootPath}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code }),
        });
      } catch (error) {
        console.warn('[adawat-pdp] discount code failed', error);
      }
    }

    if (bundleRoot) {
      bundleRoot.addEventListener('click', (event) => {
        const card = event.target.closest('[data-bundle-qty]');
        if (!card) return;
        selectBundleCard(card);
      });
      const first =
        bundleRoot.querySelector('.adawat-pdp-bundle__card.is-selected') ||
        bundleRoot.querySelector('[data-bundle-qty]');
      if (first) selectBundleCard(first);
    }

    // Main form ATC — bind click as well as submit. Desktop Chrome/Safari
    // can skip the submit event when onsubmit="return false;" is present.
    let adding = false;
    async function handleAddToCart(e) {
      e.preventDefault();
      e.stopPropagation();
      if (adding || !atcBtn) return;

      if (!optionsComplete() || !findVariant(selectedOptions)) {
        promptSelectOptions();
        return;
      }

      if (atcBtn.disabled) return;

      const variantId = Number(variantInput?.value);
      if (!variantId) {
        promptSelectOptions();
        return;
      }

      const items = cartItemsFromBundle(variantId);
      adding = true;
      atcBtn.classList.add('is-loading');
      if (formError) {
        formError.hidden = true;
        formError.textContent = '';
      }

      try {
        await addItemsToCart(items);
        await applyBundleDiscountCode();
        const original = atcLabel?.textContent;
        if (atcLabel) atcLabel.textContent = 'Added';
        setTimeout(() => {
          if (atcLabel && original) atcLabel.textContent = original;
        }, 1200);
      } catch (err) {
        if (formError) {
          formError.textContent = err.message || 'Could not add to cart';
          formError.hidden = false;
        }
      } finally {
        adding = false;
        atcBtn.classList.remove('is-loading');
      }
    }
    form?.addEventListener('submit', handleAddToCart);
    atcBtn?.addEventListener('click', handleAddToCart);

    buyNowBtn?.addEventListener('click', async () => {
      if (!buyNowBtn) return;
      if (!optionsComplete() || !findVariant(selectedOptions)) {
        promptSelectOptions();
        return;
      }
      if (buyNowBtn.disabled) return;
      const variantId = Number(variantInput?.value);
      if (!variantId) {
        promptSelectOptions();
        return;
      }
      buyNowBtn.disabled = true;
      buyNowBtn.classList.add('is-loading');
      if (formError) {
        formError.hidden = true;
        formError.textContent = '';
      }
      try {
        await addItemsToCart(cartItemsFromBundle(variantId), { openDrawer: false });
        await applyBundleDiscountCode();
        const minCheck = await cartMeetsMinimum();
        if (!minCheck.ok) {
          openCartDrawer();
          throw new Error(minCheck.message || 'Minimum order not met');
        }
        const rootPath = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const checkoutUrl = root.dataset.checkoutUrl || `${rootPath}checkout`;
        window.location.assign(checkoutUrl);
      } catch (err) {
        buyNowBtn.disabled = false;
        buyNowBtn.classList.remove('is-loading');
        if (formError) {
          formError.textContent = err.message || 'Could not checkout';
          formError.hidden = false;
        }
      }
    });

    // Often bought with — bind ADD+ + fetch same-category product if empty
    function bindObwButtons(scope) {
      (scope || root).querySelectorAll('[data-adawat-obw-add]').forEach((btn) => {
        if (btn.dataset.bound === 'true') return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', async () => {
          const id = Number(btn.dataset.variantId);
          if (!id) return;
          const item = { id, quantity: 1 };
          btn.classList.add('is-loading');
          btn.disabled = true;
          try {
            await addItemsToCart(item);
            const originalHtml = btn.innerHTML;
            const originalLabel = btn.getAttribute('aria-label');
            const isIconBtn = btn.classList.contains('adawat-pdp__pair-add');
            if (!isIconBtn) {
              btn.innerHTML = btn.dataset.addedLabel || 'Added';
              btn.setAttribute('aria-label', btn.dataset.addedLabel || 'Added');
            }
            setTimeout(() => {
              if (!isIconBtn) {
                btn.innerHTML = originalHtml;
                if (originalLabel) btn.setAttribute('aria-label', originalLabel);
              }
              btn.disabled = false;
              btn.classList.remove('is-loading');
            }, 1200);
          } catch (err) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            console.warn('[adawat-pdp] OBW add failed', err);
          }
        });
      });
    }

    function renderObwProduct(obwRoot, rec) {
      const variants = rec.variants || [];
      const variant = variants.find((v) => v.available) || variants[0];
      if (!variant) return false;
      const img =
        rec.featured_image ||
        (rec.images && (typeof rec.images[0] === 'string' ? rec.images[0] : rec.images[0]?.src));
      const price = formatMoney(variant.price, moneyFormat);
      const heading = obwRoot.dataset.heading || 'Often bought with';
      const title = rec.title || '';
      obwRoot.innerHTML = `
        <p class="adawat-pdp__obw-label">${heading}</p>
        <div class="adawat-pdp__obw-row">
          <a href="${rec.url}" class="adawat-pdp__obw-thumb">${img ? `<img src="${img}" alt="" width="56" height="56" loading="lazy">` : ''}</a>
          <div class="adawat-pdp__obw-info">
            <a href="${rec.url}" class="adawat-pdp__obw-title"></a>
            <span class="adawat-pdp__obw-price">${price}</span>
          </div>
          <button type="button" class="adawat-pdp__obw-add" data-adawat-obw-add data-variant-id="${variant.id}" data-added-label="Added" aria-label="Add to cart">ADD+</button>
        </div>`;
      obwRoot.querySelector('.adawat-pdp__obw-title').textContent = title;
      obwRoot.hidden = false;
      obwRoot.classList.remove('is-empty');
      bindObwButtons(obwRoot);
      return true;
    }

    bindObwButtons();

    const obwRoot = root.querySelector('[data-adawat-obw]');
    if (obwRoot && (obwRoot.hidden || obwRoot.classList.contains('is-empty'))) {
      const productId = String(obwRoot.dataset.productId || '');
      const collectionHandle = obwRoot.dataset.collectionHandle || '';
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const loadFromCollection = collectionHandle
        ? fetch(`${rootUrl}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=12`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              const products = (data && data.products) || [];
              const match = products.find((p) => String(p.id) !== productId && (p.variants || []).some((v) => v.available));
              return match || null;
            })
        : Promise.resolve(null);

      const loadRelated = () =>
        fetch(`${rootUrl}recommendations/products.json?product_id=${productId}&limit=4&intent=related`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            const products = (data && data.products) || [];
            return products.find((p) => String(p.id) !== productId) || null;
          });

      loadFromCollection
        .then((rec) => rec || loadRelated())
        .then((rec) => {
          if (rec) renderObwProduct(obwRoot, rec);
        })
        .catch(() => {});
    }

    window.addEventListener('adawat:sitewide-sale', (event) => {
      const variant = previewVariant();
      const id = Number(variant?.id || 0);
      const specific =
        id && event.detail?.byVariant ? clampSale(event.detail.byVariant[String(id)]) : 0;
      const baked = clampSale(root.dataset.adawatSalePercent);
      if (specific > baked) root.dataset.adawatSalePercent = String(specific);
      updateUI();
    });

    updateUI();
  }

  function boot() {
    document.querySelectorAll('[data-adawat-pdp]').forEach(initPdp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

document.addEventListener('shopify:section:load', (e) => {
  const root = e.target?.querySelector?.('[data-adawat-pdp]') || (e.target?.matches?.('[data-adawat-pdp]') ? e.target : null);
  if (root) {
    root.dataset.adawatPdpInit = 'false';
    initPdp(root);
  }
});
